import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { twMerge } from 'tailwind-merge';
import {
  Spring,
  SPRING_DEFAULT,
  SPRING_MOMENTUM,
  SPRING_SNAPPY,
  VelocityTracker,
  projectMomentum,
  rubberband,
} from '../lib/spring';

interface SheetProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  ariaLabel?: string;
  /** Extra classes for the panel (e.g. a fixed height). */
  className?: string;
  /** Show the grabber pill at the top of the panel. */
  showHandle?: boolean;
  /** Fill the available height instead of sizing to content. */
  fullHeight?: boolean;
  /** Unsaved work: dragging down resists and springs back, and the backdrop won't close it. */
  preventDismiss?: boolean;
  zIndex?: number;
}

const SHEET_MAX_HEIGHT = 'calc(100dvh - env(safe-area-inset-top, 0px) - 12px)';
const DRAG_THRESHOLD = 8; // px of movement before we commit to a direction
const NO_DRAG_SELECTOR = 'input[type="range"], textarea, select, [data-sheet-no-drag]';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * Nearest vertically scrollable ancestor of `el` inside `panel`.
 * Returns 'nested' when `el` lives in a fixed-position dialog layered on top of the sheet.
 */
function inspectTarget(el: Element | null, panel: HTMLElement): HTMLElement | null | 'nested' {
  let scrollable: HTMLElement | null = null;
  while (el && el !== panel) {
    if (el instanceof HTMLElement) {
      const style = getComputedStyle(el);
      if (style.position === 'fixed') return 'nested';
      if (!scrollable && el.scrollHeight > el.clientHeight + 1 && (style.overflowY === 'auto' || style.overflowY === 'scroll')) {
        scrollable = el;
      }
    }
    el = el.parentElement;
  }
  return scrollable;
}

/**
 * Bottom sheet that tracks the finger 1:1, can be grabbed mid-animation,
 * and settles with springs that inherit the release velocity.
 */
export const Sheet: React.FC<SheetProps> = ({
  isOpen,
  onClose,
  children,
  ariaLabel,
  className = '',
  showHandle = true,
  fullHeight = false,
  preventDismiss = false,
  zIndex = 50,
}) => {
  const [rendered, setRendered] = useState(isOpen);
  if (isOpen && !rendered) setRendered(true);

  // Keep showing the last open content while the sheet animates away, so it
  // doesn't flash to an empty/reset state as the parent clears its state.
  const lastChildren = useRef(children);
  if (isOpen) lastChildren.current = children;

  const panelRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const heightRef = useRef(0);
  const draggingRef = useRef(false);
  const reducedRef = useRef(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;
  const preventDismissRef = useRef(preventDismiss);
  preventDismissRef.current = preventDismiss;

  const springRef = useRef<Spring | null>(null);
  if (!springRef.current) {
    springRef.current = new Spring(0, (y) => {
      const panel = panelRef.current;
      const h = heightRef.current || 1;
      const progress = Math.min(1, Math.max(0, 1 - y / h));
      if (backdropRef.current) backdropRef.current.style.opacity = String(progress);
      if (!panel) return;
      if (reducedRef.current && !draggingRef.current) {
        // Reduced motion: cross-fade in place instead of sliding.
        panel.style.transform = '';
        panel.style.opacity = String(progress);
      } else {
        panel.style.opacity = '';
        // Drop the transform at rest so position:fixed dialogs nested inside
        // the sheet are positioned against the viewport, not the panel.
        panel.style.transform = Math.abs(y) < 0.01 ? '' : `translate3d(0, ${y}px, 0)`;
      }
    });
  }

  // Open / close animations.
  useLayoutEffect(() => {
    const spring = springRef.current!;
    const panel = panelRef.current;
    if (!rendered || !panel) return;
    reducedRef.current = prefersReducedMotion();
    heightRef.current = panel.offsetHeight;

    if (isOpen) {
      // Freshly mounted: start fully off-screen. Otherwise (re-opened mid-close) continue from where it is.
      if (!panel.dataset.shown) spring.set(heightRef.current);
      panel.dataset.shown = '1';
      spring.animateTo(0, SPRING_DEFAULT);
      panel.focus({ preventScroll: true });
    } else {
      spring.animateTo(heightRef.current, SPRING_SNAPPY, undefined, () => setRendered(false));
    }
  }, [isOpen, rendered]);

  useEffect(() => () => springRef.current?.stop(), []);

  // Escape to dismiss (hardware keyboards / desktop).
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen]);

  // Drag-to-dismiss gesture.
  useEffect(() => {
    const panel = panelRef.current;
    if (!rendered || !panel) return;
    const spring = springRef.current!;
    const tracker = new VelocityTracker();

    let state: 'idle' | 'pending' | 'dragging' | 'ignore' = 'idle';
    let startX = 0;
    let startY = 0;
    let originY = 0; // finger Y when the drag was committed
    let baseY = 0; // sheet offset when the drag was committed
    let scrollEl: HTMLElement | null = null;
    let wasAnimating = false;

    const begin = (x: number, y: number, target: EventTarget | null) => {
      if (!isOpenRef.current) return;
      const el = target instanceof Element ? target : null;
      if (el?.closest(NO_DRAG_SELECTOR)) {
        state = 'ignore';
        return;
      }
      const inspected = inspectTarget(el, panel);
      if (inspected === 'nested') {
        state = 'ignore';
        return;
      }
      state = 'pending';
      heightRef.current = panel.offsetHeight;
      startX = x;
      startY = y;
      scrollEl = inspected;
      // Grab the sheet where it is right now, even mid-animation.
      wasAnimating = spring.isAnimating;
      spring.stop();
    };

    const move = (x: number, y: number, prevent: () => void) => {
      if (state === 'idle' || state === 'ignore') return;
      const dx = x - startX;
      const dy = y - startY;

      if (state === 'pending') {
        const atTop = !scrollEl || scrollEl.scrollTop <= 0;
        const sheetDisplaced = spring.value > 0.5;
        const vertical = Math.abs(dy) >= Math.abs(dx);
        // Claim downward moves at the top of the content before the browser starts a scroll.
        if (vertical && dy > 0 && atTop) prevent();
        if (Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) return;
        if (!vertical || (!sheetDisplaced && (dy < 0 || !atTop))) {
          state = 'ignore';
          if (wasAnimating) spring.animateTo(spring.target, SPRING_DEFAULT);
          return;
        }
        state = 'dragging';
        draggingRef.current = true;
        originY = y;
        baseY = spring.value;
        tracker.reset();
        panel.style.willChange = 'transform';
      }

      prevent();
      let next = baseY + (y - originY);
      if (next < 0) next = rubberband(next, heightRef.current);
      else if (preventDismissRef.current) next = rubberband(next, heightRef.current, 0.2);
      spring.set(next);
      tracker.add(y);
    };

    const end = () => {
      if (state === 'dragging') {
        const v = tracker.velocity();
        const projected = spring.value + projectMomentum(v);
        draggingRef.current = false;
        panel.style.willChange = '';
        suppressNextClick();
        if (!preventDismissRef.current && projected > heightRef.current * 0.5 && v > -50) {
          spring.velocity = v;
          onCloseRef.current();
        } else {
          // Settle back; only allow overshoot when the release carried momentum.
          spring.animateTo(0, Math.abs(v) > 300 ? SPRING_MOMENTUM : SPRING_DEFAULT, v);
        }
      } else if (state === 'pending' && wasAnimating) {
        // A tap during the open animation shouldn't freeze the sheet.
        spring.animateTo(spring.target, SPRING_DEFAULT);
      }
      state = 'idle';
    };

    const suppressNextClick = () => {
      const block = (e: Event) => {
        e.stopPropagation();
        e.preventDefault();
      };
      panel.addEventListener('click', block, { capture: true, once: true });
      setTimeout(() => panel.removeEventListener('click', block, { capture: true }), 350);
    };

    // Touch (non-passive so we can stop native scrolling once we own the gesture).
    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) {
        state = 'ignore';
        return;
      }
      begin(e.touches[0].clientX, e.touches[0].clientY, e.target);
    };
    const onTouchMove = (e: TouchEvent) => {
      const t = e.touches[0];
      if (t) move(t.clientX, t.clientY, () => e.cancelable && e.preventDefault());
    };

    // Mouse (desktop testing).
    const onPointerDown = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      begin(e.clientX, e.clientY, e.target);
      const onMove = (ev: PointerEvent) => move(ev.clientX, ev.clientY, () => ev.preventDefault());
      const onUp = () => {
        end();
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    };

    panel.addEventListener('touchstart', onTouchStart, { passive: true });
    panel.addEventListener('touchmove', onTouchMove, { passive: false });
    panel.addEventListener('touchend', end);
    panel.addEventListener('touchcancel', end);
    panel.addEventListener('pointerdown', onPointerDown);
    return () => {
      panel.removeEventListener('touchstart', onTouchStart);
      panel.removeEventListener('touchmove', onTouchMove);
      panel.removeEventListener('touchend', end);
      panel.removeEventListener('touchcancel', end);
      panel.removeEventListener('pointerdown', onPointerDown);
    };
  }, [rendered]);

  if (!rendered) return null;

  return createPortal(
    <div
      className="fixed inset-0 flex flex-col justify-end font-[-apple-system,BlinkMacSystemFont,'SF_Pro_Text',sans-serif]"
      style={{ zIndex, pointerEvents: isOpen ? 'auto' : 'none' }}
    >
      <div
        ref={backdropRef}
        className="absolute inset-0 bg-black/50"
        style={{ opacity: 0 }}
        onClick={() => {
          if (!preventDismissRef.current) onCloseRef.current();
        }}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        tabIndex={-1}
        className={twMerge(
          'relative w-full max-w-md mx-auto flex flex-col bg-[#F8F9FA] dark:bg-[#12141A] text-slate-900 dark:text-white rounded-t-[32px] border-t border-slate-200/80 dark:border-zinc-800/80 shadow-[0_-8px_40px_rgba(0,0,0,0.25)] outline-none pb-[env(safe-area-inset-bottom)]',
          className
        )}
        style={{
          transform: 'translate3d(0, 100%, 0)',
          maxHeight: SHEET_MAX_HEIGHT,
          height: fullHeight ? SHEET_MAX_HEIGHT : undefined,
        }}
      >
        {/* Skirt: extends the panel below the screen edge so overshoot never reveals a gap */}
        <div className="absolute left-0 right-0 top-full h-[120px] bg-inherit" aria-hidden="true" />
        {showHandle && (
          <div className="w-full pt-2.5 pb-1 flex justify-center shrink-0 cursor-grab" aria-hidden="true">
            <div className="w-9 h-[5px] rounded-full bg-slate-300 dark:bg-zinc-700" />
          </div>
        )}
        {lastChildren.current}
      </div>
    </div>,
    document.body
  );
};
